import { useState } from "react";
import {
  useReferenceableArticles,
  useReferenceablePlaylists,
  MIN_ARTICLE_QUERY_LENGTH,
} from "./useReferenceableSources";
import { referenceKey, type ChatReferenceKind } from "./useChatReferences";

// Inline `@`/`+` mention for the composer. Typing `@` or `+` (at a word
// boundary) opens an autocomplete of the user's playlists and of the articles
// matching what follows; picking one STRIPS the trigger token from the text and
// adds the reference out-of-band (a `ConversationReference` row), never leaving
// "@name" in the sent message — the same model as a file chip.
//
// The two kinds appear in ONE list rather than behind a mode switch: mid-typing
// the user knows what they are looking for by name, not by which table it lives
// in. Playlists come first (a closed, short catalogue the user owns) and articles
// after (a corpus search, so only once the query is long enough to mean
// something).
//
// Detection is anchored to the END of the value (the common case: the user is
// typing the mention where the caret is), which lets the hook work off the plain
// composer string with no textarea ref or caret tracking. Selection is by TAP
// (this is a mobile webview app); no keyboard navigation of the list.

// Trigger char at a word boundary, followed by the (space-free) query it filters
// on, anchored to the end. The query is capped so a long paste can't run the
// filter over the whole message.
const TRIGGER_RE = /(?:^|\s)([@+])([^\s@+]{0,60})$/;

/** One row of the dropdown — what tapping it will reference. */
export type ChatMentionResult = {
  kind: ChatReferenceKind;
  id: string;
  title: string;
};

export type ChatMention = {
  // The rows to show, or null when no trigger is active (the dropdown renders
  // nothing).
  results: ChatMentionResult[] | null;
  onSelect: (result: ChatMentionResult) => void;
  // Dismiss the current trigger (e.g. an explicit close) until the text changes.
  dismiss: () => void;
};

export function useChatMention(opts: {
  // False where the surface offers no references (CWYOP, shared view): the
  // trigger never activates and no query runs.
  enabled: boolean;
  value: string;
  setValue: (value: string) => void;
  onAdd: (kind: ChatReferenceKind, targetId: string, title: string) => void;
  // Targets already referenced (or being added), as `kind:id` keys — excluded
  // from the list.
  referencedKeys: string[];
  // The assistant engine's Redaction, null elsewhere: a query inside a Match
  // never reaches the article search.
  overlapsMatch: ((text: string, start: number, end: number) => boolean) | null;
}): ChatMention {
  // The value at which the user dismissed the dropdown; it stays closed until the
  // text changes away from it. Not derived — it is a user gesture with no other
  // source of truth.
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);

  const match = opts.enabled ? TRIGGER_RE.exec(opts.value) : null;
  const active = match !== null && opts.value !== dismissedAt;
  const query = active && match !== null ? match[2] : null;

  const { playlists } = useReferenceablePlaylists(query !== null);
  // A bare `@` lists the user's playlists; searching the whole corpus needs
  // something to search for, so articles only join in once the query is real.
  const { articles } = useReferenceableArticles(
    query !== null &&
      query.length >= MIN_ARTICLE_QUERY_LENGTH &&
      (opts.overlapsMatch === null ||
        !opts.overlapsMatch(
          opts.value,
          opts.value.length - query.length,
          opts.value.length
        ))
      ? query
      : null
  );

  const referenced = new Set(opts.referencedKeys);
  const results =
    query === null
      ? null
      : [
          ...playlists
            .filter((playlist) => {
              if (query === "") return true;
              return playlist.title.toLowerCase().includes(query.toLowerCase());
            })
            .map((playlist): ChatMentionResult => ({
              kind: "playlist",
              id: playlist.id,
              title: playlist.title,
            })),
          // Already filtered by the server against the same query.
          ...articles.map((article): ChatMentionResult => ({
            kind: "article",
            id: article.id,
            title: article.title,
          })),
        ].filter(
          (result) => !referenced.has(referenceKey(result.kind, result.id))
        );

  const onSelect = (result: ChatMentionResult): void => {
    if (match === null) return;
    // Strip the trigger token (the trigger char + its query) off the end,
    // keeping any preceding separator. `match[2]` is the query, `match[1]` the
    // trigger char — together `query.length + 1` chars at the end.
    const tokenLength = match[2].length + 1;
    opts.setValue(opts.value.slice(0, opts.value.length - tokenLength));
    opts.onAdd(result.kind, result.id, result.title);
    setDismissedAt(null);
  };

  return {
    results,
    onSelect,
    dismiss: () => {
      setDismissedAt(opts.value);
    },
  };
}
